import type { ModuleStorageTransport } from "@artis/sdk";
import type { GraphQLClient } from "@package/graphql";

import type { JSONValue } from "../../__generated__/gql/graphql";
import { ModuleStorageGet, ModuleStorageSet, ModuleStorageStream } from "./queries";

type ModuleSubscriber = (key: string, value: unknown, writeId: string) => void;

type Row = { module: string; key: string; value?: unknown; rev: string };

const isRow = (data: unknown): data is Row => {
  const row = data as Partial<Row> | null;
  return (
    typeof row?.module === "string" &&
    typeof row.key === "string" &&
    typeof row.rev === "string" &&
    /^\d+$/.test(row.rev)
  );
};

const upsert = async (client: GraphQLClient, module: string, key: string, value: unknown): Promise<string> => {
  // jsonb takes any JSON, but the generated JSONValue scalar only models one level of
  // nesting; the SDK already constrains values to JSON-serializable ones.
  const { data } = await client.execute(ModuleStorageSet, { module, key, value: value as JSONValue });
  const rev = data?.insert_module_storage_one?.rev;
  if (!rev) throw new Error(`module storage: upsert of ${module}/${key} returned no rev`);
  return rev;
};

// True when `rev` is strictly greater than `than` (bigint compare; `null` means "nothing seen yet").
const isNewer = (rev: string, than: string | null): boolean => than === null || BigInt(rev) > BigInt(than);

/**
 * The `user`-scope storage transport over Hasura, backed by the `module_storage` table
 * keyed by `(user_id, module, key)`. The caller's user id never crosses the wire: the
 * select permission scopes reads to the caller and an insert column preset fills it on
 * writes, so the transport only ever names a `(module, key)` pair.
 *
 * `set` resolves the row's `rev`, a monotonic bigint the `module_storage_touch` trigger
 * assigns from a global sequence on every insert and update. The SDK uses it as the write
 * id for its echo guard, and it doubles as the cursor for the streaming subscription.
 *
 * `get` rejects when the server cannot be reached, rather than resolving `undefined`.
 * Callers must not treat a rejection as "no stored value": overwriting afterwards would
 * replace good server data with whatever the caller started up with.
 *
 * `subscribe` attaches to a single shared stream that covers every module (the rev is
 * user-scoped, so one stream is enough); it demultiplexes rows by module. See
 * {@link ModuleStorageStream}.
 *
 * Tabs of the same browser do not wait for that stream: a successful write is also posted on
 * a `BroadcastChannel` named after the user, and sibling tabs deliver it at once. The stream
 * still carries every row, so a row is delivered once per key, newest `rev` wins: the
 * stream's copy of a broadcast row is dropped, and an older row never overwrites a newer
 * one. A broadcast never moves the stream cursor, so rows of other keys, written on other
 * devices, are not skipped.
 */
export const createHasuraStreamTransport = (client: GraphQLClient, userId?: string): ModuleStorageTransport => {
  const subscribers = new Map<string, Set<ModuleSubscriber>>();
  const deliveredRev = new Map<string, Map<string, string>>();

  let lastRev: string | null = null;
  let streamUnsub: (() => void) | null = null;
  let channel: BroadcastChannel | null = null;

  const deliver = (row: Row) => {
    const revs = deliveredRev.get(row.module) ?? new Map<string, string>();
    if (!isNewer(row.rev, revs.get(row.key) ?? null)) return;
    revs.set(row.key, row.rev);
    deliveredRev.set(row.module, revs);
    for (const onChange of [...(subscribers.get(row.module) ?? [])]) onChange(row.key, row.value ?? null, row.rev);
  };

  const openChannel = () => {
    if (!userId || typeof BroadcastChannel === "undefined") return null;
    return new BroadcastChannel(`artis-module-storage:${userId}`);
  };

  // A failed broadcast only costs speed: the stream still delivers the row.
  const broadcast = (row: Row) => {
    try {
      const out = channel ?? openChannel();
      out?.postMessage(row);
      if (out !== channel) out?.close();
    } catch {}
  };

  const write = async (module: string, key: string, value: unknown) => {
    const rev = await upsert(client, module, key, value);
    broadcast({ module, key, value, rev });
    return rev;
  };

  const openStream = () => {
    streamUnsub = client.subscribe(
      ModuleStorageStream,
      { cursorRev: lastRev ?? "0" },
      {
        next: ({ data }) => {
          for (const row of data?.module_storage_stream ?? []) {
            if (!isNewer(row.rev, lastRev)) continue;
            lastRev = row.rev;
            deliver(row);
          }
        },
      },
    );
    channel = openChannel();
    if (channel) channel.onmessage = ({ data }) => isRow(data) && deliver(data);
  };

  return {
    get: async (module, key) => {
      const { data } = await client.execute(ModuleStorageGet, { module, key });
      const row = data?.module_storage[0];
      // Only seed while the stream is closed; once open, the stream alone advances lastRev.
      if (!streamUnsub && row?.rev != null && isNewer(row.rev, lastRev)) lastRev = row.rev;
      return row?.value ?? undefined;
    },

    set: write,

    /**
     * Writes a `null` tombstone instead of deleting the row. A cursor-based stream can only
     * emit rows that still exist, so a hard delete would never reach other tabs or devices,
     * whereas a tombstone bumps `rev` and travels like any other write. Its `rev` is returned
     * as the write id so the SDK can echo-guard the tombstone like any other write.
     */
    remove: (module, key) => write(module, key, null),

    subscribe: (module, onChange) => {
      let set = subscribers.get(module);
      if (!set) {
        set = new Set();
        subscribers.set(module, set);
      }
      set.add(onChange);
      if (!streamUnsub) openStream();
      return () => {
        set.delete(onChange);
        if (set.size === 0) subscribers.delete(module);
        if (subscribers.size === 0) {
          streamUnsub?.();
          streamUnsub = null;
          channel?.close();
          channel = null;
        }
      };
    },
  };
};
